/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useState, lazy } from 'react';
import { ResponsiveGrid, EditableComponent } from '@adobe/aem-react-editable-components';
import Glide from '@glidejs/glide';
import clsx from 'clsx';
import { MiniCardCarouselProps, MiniCardCarouselContainerProps } from './MiniCardCarouselContainer.types';
import { StyledCardCarousel } from './MiniCardCarouselContainer.styles';
import { Heading, Button, headingType, size, Types, useEditorMode } from '@marriott/mi-ui-library';

const CardHorizontalMini = lazy(() =>
  import('@marriott/mi-ui-library').then(module => ({
    default: module['CardHorizontalMini'] || (() => null),
  }))
);

const MiniCardCarouselContainerComp: FC<MiniCardCarouselProps> = (props: MiniCardCarouselProps) => {
  const {
    titleText,
    subTitleText,
    ctaLabel,
    ctaLink,
    eyebrow,
    ctaType,
    cardsCount,
    openInaNewTab,
    totalNumberOfCards,
    trackingProperties,
    styleclass,
    cqItems,
    componentId,
    pagePath,
    itemPath,
    isAuthorMode,
    featuredCard,
    defaultCardsPerView = 0,
    isFullWidthCards = false,
  } = props;
  const cardsNum = featuredCard && cardsCount > 0 ? 1 : 2;
  const initialCardsNum = defaultCardsPerView > 0 ? defaultCardsPerView : cardsNum;
  const [cardsPerView, setCardsPerView] = useState(initialCardsNum);
  const [viewPort, setViewPort] = useState('Desktop');

  const { Themes } = Types;
  const { isInEditor } = useEditorMode();

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('onorientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('onorientationchange', handleResize);
    };
  });

  const handleResize = () => {
    const sm = window.matchMedia('(max-width: 575px)');
    const md = window.matchMedia('(min-width:576px) and (max-width: 767px)');
    const lg = window.matchMedia('(min-width: 768px) and (max-width: 991px)');
    const xl = window.matchMedia('(min-width: 992px)');
    if (sm?.matches || md?.matches) {
      setViewPort('Mobile');
      setCardsPerView(1);
    } else if (lg?.matches) {
      setViewPort('Tablet');
      setCardsPerView(1);
    } else if (xl?.matches) {
      setViewPort('Desktop');
      const cardsNum = featuredCard && cardsCount > 0 ? 1 : 2;
      const desktopCardsNum = defaultCardsPerView > 0 ? defaultCardsPerView : cardsNum;
      setCardsPerView(desktopCardsNum);
    }
  };

  useEffect(() => {
    const pageWcmMode = document.getElementsByTagName('html')[0];
    const updateStyleComponent = document.getElementById(`${componentId}__slides`);
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          const currWcmMode = pageWcmMode?.getAttribute('class');
          if (currWcmMode?.includes('Edit')) {
            updateStyleComponent?.classList.add('glide__slides_authoring');
          } else if (currWcmMode?.includes('Preview')) {
            updateStyleComponent?.classList.remove('glide__slides_authoring');
          }
        }
      });
    });
    observer.observe(pageWcmMode, { attributes: true });
    handleResize();

    return () => {
      observer.disconnect();
    };
  }, [componentId]);

  useEffect(() => {
    const carouselRootSelector = `#${componentId}`;
    if (document?.querySelector(carouselRootSelector)) {
      try {
        const glide = new Glide(carouselRootSelector, {
          type: 'slider',
          bound: true,
          perView: cardsPerView,
          startAt: 0,
          rewind: false,
          gap: 33,
          autoplay: false,
          dragThreshold: false,
          breakpoints: {
            768: {
              gap: 14,
              perView: 1,
            },
          },
        }).mount({ ArrowDisabler });

        const observer = new MutationObserver(() => glide.update({}));
        const sliderRootNode = document.querySelector(`${carouselRootSelector} .glide__track ul`);
        sliderRootNode && observer.observe(sliderRootNode, { childList: true });

        document
          ?.querySelector(`#${componentId}_left_arrow`)
          ?.addEventListener('touchstart', (e: any) => e.preventDefault());
        document
          ?.querySelector(`#${componentId}_right_arrow`)
          ?.addEventListener('touchstart', (e: any) => e.preventDefault());

        return () => {
          observer.disconnect();
        };
      } catch (error) {
        // log.error(`Error in initializing Glide for ${componentId}`, error);
      }
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorMode, componentId, cardsPerView]);

  const buttonCallback = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    window.open(ctaLink, openInaNewTab ? '_blank' : '_self');
  };

  const ArrowDisabler = function (Glide: any, Components: any) {
    return {
      mount() {
        // Only in effect when rewinding is disabled
        if (Glide.settings.rewind) {
          return;
        }
        Glide.on(['mount.after', 'run'], () => {
          // Filter out arrows_control
          for (const controlItem of Components.Controls.items) {
            if (controlItem.className !== 'glide__arrows') {
              continue;
            }

            // Set left arrow state
            const left = controlItem?.querySelector('.glide__arrow--left');
            if (left) {
              if (Glide.index === 0) {
                left.classList.add('.is-disabled'); // Disable on first slide
              } else {
                left.classList.remove('.is-disabled'); // Enable on other slides
              }
            }

            // Set right arrow state
            const right = controlItem?.querySelector('.glide__arrow--right');
            if (right) {
              if (Glide.index === Components.Sizes.length - Glide.settings.perView) {
                right.classList.add('.is-disabled'); // Disable on last slide
              } else {
                right.classList.remove('.is-disabled'); // Disable on other slides
              }
            }
          }
        });
      },
    };
  };

  const customAttributes = trackingProperties?.enableScrollingBehavior ? { 'data-section-tracking': componentId } : {};

  // This function is used to render the component in authoring mode
  const cardContainer = (index: number, innerCompStyleClass: string | undefined) => {
    return (
      <ResponsiveGrid
        key={`${itemPath}/${totalNumberOfCards[index]}`}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pagePath={pagePath}
        itemPath={`${itemPath}/${totalNumberOfCards[index]}`}
        columnCount="12"
        gridClassNames={''}
        customClassName={clsx(innerCompStyleClass, 'lt-link-container')}
        config={{
          isEmpty: () => true,
          resourceType: 'mi-aem-martech/components/container',
        }}
        isInEditor={isInEditor}
      />
    );
  };

  // This function is used to render the component in end-user mode
  const cardComponent = (cardName: string, jsonData: any) => {
    // eslint-disable-next-line no-prototype-builtins
    if (jsonData?.hasOwnProperty(cardName)) {
      const card = jsonData[cardName];
      const cardItems = card[':items'];
      for (const itemKey in cardItems) {
        // eslint-disable-next-line no-prototype-builtins
        if (cardItems?.hasOwnProperty(itemKey)) {
          const item = cardItems[itemKey];

          return (
            <CardHorizontalMini
              componentId={`lt-link-${cardName}`}
              key={`lt-link-${cardName}`}
              header={item.header}
              headerShort={item.headerShort}
              headerTag={item.headerTag}
              ctaLink={item.ctaLink}
              img={item.icon}
              dynamicMedia={item.dynamicMedia}
              openInaNewTab={item.openInaNewTab}
              trackingProperties={item.trackingProperties}
              styleclass={`${Themes.Standard} minicardcontiner lt-link`}
              descriptionShort={item.descriptionShort}
              descriptionText={item.descriptionText}
              assetVariation={item.assetVariation}
              iconPath={item.iconPath}
              custom_click_track_value={item.custom_click_track_value}
            />
          );
        } else {
          return null;
        }
      }
    } else {
      return null;
    }
    return null;
  };

  const GetCarComponentsForDesktop = (totalNumberOfCards: any, cqItems: any) => {
    const cards = [];
    for (let i = 0; i < totalNumberOfCards?.length; i = i + 1) {
      cards.push(<li key={`card-${i}`}>{cardComponent(totalNumberOfCards[i], cqItems)}</li>);
    }
    return cards;
  };

  return (
    <StyledCardCarousel
      data-component-name="m-ui-library-MiniCardCarouselContainer"
      data-testid="ui-library-MiniCardCarouselContainer"
      className={clsx(`col-12 py-0 px-0 links-section`)}
      {...customAttributes}
    >
      {titleText && (
        <Heading customClass="lt-kicker-3" titleText={titleText} variation={headingType.title} fontSize={size.medium} />
      )}
      <div className={clsx('carousal_header')}>
        {ctaLabel && (
          <div className={clsx('carousal_header--cta')}>
            <Button
              href={ctaLink}
              isLink={ctaType === 'tertiaryLink'}
              target={openInaNewTab ? '_target' : '_self'}
              callback={buttonCallback}
              className={[
                'custom_click_track',
                ctaType === 'primaryButton' ? 'm-button-primary' : '',
                ctaType === 'secondaryButton' ? 'm-button-secondary' : '',
                ctaType === 'tertiaryLink' ? 'm-link-tertiary-button' : '',
                openInaNewTab
                  ? ctaType === 'primaryButton' || ctaType === 'secondaryButton'
                    ? 'm-button-external'
                    : 'm-link-tertiary-button-external'
                  : '',
              ]}
              custom_click_track_value={`${trackingProperties?.trackingContentPosition || ''}|${
                trackingProperties?.trackingDescription || ''
              }|${openInaNewTab ? 'external' : 'internal'}`}
              buttonCopy={ctaLabel}
              isTextBeforeChildren={false}
            />
          </div>
        )}
      </div>
      <div id={componentId} className={clsx('glide')}>
        <div className={clsx(!subTitleText && !eyebrow ? 'glide__track__pt3' : 'glide__track')} data-glide-el="track">
          <ul
            className={clsx('glide__slides', 'pb-3', isFullWidthCards ? 'full-width-cards-slides' : '')}
            id={`${componentId}__slides`}
          >
            {isAuthorMode &&
              !isFullWidthCards &&
              Array.from({ length: totalNumberOfCards?.length }, (_, i) => cardContainer(i, styleclass))}
            {!isAuthorMode && GetCarComponentsForDesktop(totalNumberOfCards, cqItems)}
          </ul>
        </div>
        {viewPort === 'Desktop' && cardsCount !== cardsPerView && cardsCount > 2 && !featuredCard && (
          <div className="center-align">
            <div className={`carouselControlType1 ${isFullWidthCards ? 'full-width-cards-controls' : ''}`}>
              <div className="glide__arrows" data-glide-el="controls">
                <button
                  className={clsx(
                    'left-arrow',
                    'glide__arrow2 glide__arrow--left',
                    `${trackingProperties?.clickTrack ? 'custom_click_track' : ''}`
                  )}
                  id={`${componentId}_left_arrow`}
                  data-glide-dir="<"
                  aria-label="left-arrow"
                  data-custom_click_track_value={`${trackingProperties?.trackingContentPosition || ''}|${
                    trackingProperties?.leftArrowDesc || ''
                  }|internal`}
                >
                  <span className="icon-arrow-left" id={`${componentId}_left_arrow`} data-id="left"></span>
                </button>
              </div>
              <div data-glide-el="controls[nav]">
                {Array.from({ length: cardsCount - (cardsPerView - 1) }, (_, i) => (
                  <button
                    className=""
                    data-glide-dir={i}
                    aria-label={`control-button-${i}`}
                    key={cardsPerView + i}
                  ></button>
                ))}
              </div>
              <div className="glide__arrows" data-glide-el="controls">
                <button
                  className={clsx(
                    'right-arrow',
                    'glide__arrow2 glide__arrow--right',
                    `${trackingProperties?.clickTrack ? 'custom_click_track' : ''}`
                  )}
                  id={`${componentId}_right_arrow`}
                  data-glide-dir=">"
                  aria-label="right-arrow"
                  data-custom_click_track_value={`${trackingProperties?.trackingContentPosition || ''}|${
                    trackingProperties?.rightArrowDesc || ''
                  }|internal`}
                >
                  <span className="icon-arrow-right" data-id="right"></span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </StyledCardCarousel>
  );
};

export const MiniCardCarouselContainerConfig = {
  emptyLabel: 'MiniCardCarousalContainer',
  isEmpty: () => true,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MiniCardCarouselEditableComponent = (props: any) => {
  return (
    <EditableComponent config={MiniCardCarouselContainerConfig} {...props}>
      <MiniCardCarouselContainerComp {...props} componentName={props?.model?.cqType.split('/').pop()} />
    </EditableComponent>
  );
};

export const MiniCardCarouselContainer = (props: MiniCardCarouselContainerProps) => {
  const { model } = props;
  return (
    <div
      className={clsx(
        [model?.styleclass?.includes('inverse') ? 'inverse' : ''],
        [model?.styleclass?.includes('fullbleed') ? 'm-container-fullbleed' : '']
      )}
      data-testid="mini-card-carousel"
      data-component-name="o-common-static-minicardcarouselcontainer"
    >
      <div className={clsx('container', { 'p-0': !model?.styleclass?.includes('fullbleed') })}>
        <MiniCardCarouselEditableComponent {...props} />
      </div>
    </div>
  );
};
